/**
 * Super-admin card for the reassigned-number check.
 * Props: api (API.json), toast(message, type).
 * Mount inside the super-admin Calling compliance page.
 */
const { useEffect, useState } = React;

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

export function ReassignedSettingsCard({ api, toast }) {
  const [provider, setProvider] = useState('none');
  const [enforcement, setEnforcement] = useState('off');
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [dncConfigured, setDncConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api('GET', '/api/admin/compliance/reassigned-settings').then((data) => {
      if (cancelled || !data) return;
      setProvider(data.provider || 'none');
      setEnforcement(data.enforcement || 'off');
      setTwilioConfigured(!!data.twilio_configured);
      setDncConfigured(!!data.dncscrub_configured);
    }).catch((err) => {
      if (!cancelled) setError(errorMessage(err, 'Could not load reassigned-number settings.'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [api]);

  function notify(message, type) {
    if (toast) toast(message, type);
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const data = await api('PUT', '/api/admin/compliance/reassigned-settings', { provider, enforcement });
      if (data) {
        setProvider(data.provider || provider);
        setEnforcement(data.enforcement || enforcement);
        setTwilioConfigured(!!data.twilio_configured);
        setDncConfigured(!!data.dncscrub_configured);
      }
      notify('Reassigned-number settings saved.');
    } catch (err) {
      const message = errorMessage(err, 'Could not save reassigned-number settings.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="ca-card" onSubmit={save}>
      <h2>Reassigned numbers</h2>
      <p className="ca-help">Checks the FCC&apos;s database of disconnected numbers before an AI call relies on old consent. About $0.008 per check.</p>
      {loading ? <p className="ca-help">Loading…</p> : null}
      {error ? <p className="ca-error" role="alert">{error}</p> : null}
      <div className="ca-form-row">
        <label className="ca-label">
          Provider
          <select className="ca-select" value={provider} disabled={busy || loading} onChange={(event) => setProvider(event.target.value)}>
            <option value="none">None</option>
            <option value="twilio">Twilio Lookup</option>
            <option value="dncscrub">DNCScrub</option>
          </select>
        </label>
        <label className="ca-label">
          Enforcement
          <select className="ca-select" value={enforcement} disabled={busy || loading} onChange={(event) => setEnforcement(event.target.value)}>
            <option value="off">Off</option>
            <option value="warn">Warn</option>
            <option value="enforce">Enforce</option>
          </select>
        </label>
      </div>
      <p className="ca-help" role="status">Enforce blocks AI calls when the check says the number changed hands or the check fails. Warn records the result and still allows the call. Off does not check.</p>
      <p className="ca-help">
        {twilioConfigured ? 'Twilio Lookup is configured.' : 'Twilio Lookup is not configured.'}
        {' '}
        {dncConfigured ? 'DNCScrub API key is configured.' : 'DNCScrub API key is not configured.'}
        {' '}
        Twilio Reassigned Number also needs carrier registration and approval on the Twilio account.
      </p>
      <button type="submit" className="ca-btn ca-btn--primary" disabled={busy || loading}>Save reassigned-number settings</button>
    </form>
  );
}
